import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const GpsContext = createContext({ status: "off" });
export const useDriverGps = () => useContext(GpsContext);

export const useDriverState = () =>
  useQuery({ queryKey: ["driverState"], queryFn: () => api.get("/driver/state").then((r) => r.data), refetchInterval: 15000 });

const SEND_EVERY = 8000;
const STALE_MS = 60000;
const ERR = { 1: "Location permission is blocked. Allow location for this site in your browser settings.", 2: "GPS signal unavailable right now.", 3: "GPS is taking too long to respond." };

export function useOnlineGps(online) {
  const qc = useQueryClient();
  const [state, setState] = useState({ status: "off", error: "", fixAt: null, sentAt: null });
  const last = useRef(0);
  const pending = useRef(null);
  useEffect(() => {
    if (!online) {
      setState({ status: "off", error: "", fixAt: null, sentAt: null });
      return undefined;
    }
    if (!navigator.geolocation) {
      setState({ status: "unavailable", error: "This device or browser can't share GPS location." });
      return undefined;
    }
    const onFix = async ({ coords }) => {
      const now = Date.now();
      setState((s) => ({ ...s, status: "live", error: "", fixAt: now }));
      const wait = SEND_EVERY - (now - last.current);
      if (wait > 0) {
        clearTimeout(pending.current);
        pending.current = setTimeout(() => onFix({ coords }), wait);
        return;
      }
      last.current = now;
      try {
        const { data } = await api.post("/driver/location", { lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy ?? null });
        setState((s) => ({ ...s, sentAt: Date.now() }));
        if (data.timers_started?.length) {
          toast.success("You're within 100 ft. Pickup wait timer started");
          qc.invalidateQueries({ queryKey: ["driverActive"] });
        }
      } catch (e) {
        setState((s) => ({ ...s, error: formatApiError(e) }));
      }
    };
    const onErr = (err) => setState((s) => ({ ...s, status: err.code === 1 ? "denied" : "unavailable", error: ERR[err.code] || "Location unavailable." }));
    const opts = { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 };
    const id = navigator.geolocation.watchPosition(onFix, onErr, opts);
    const beat = setInterval(() => {
      navigator.geolocation.getCurrentPosition(onFix, onErr, opts);
      setState((s) => (s.status === "live" && s.fixAt && Date.now() - s.fixAt > STALE_MS ? { ...s, status: "stale" } : s));
    }, 15000);
    return () => {
      navigator.geolocation.clearWatch(id);
      clearInterval(beat);
      clearTimeout(pending.current);
    };
  }, [online, qc]);
  return state;
}

export const GPS_LABEL = {
  off: "Location sharing off (Offline)",
  live: "Sharing live location",
  stale: "Location is stale. Waiting for a fresh GPS fix",
  denied: "Location permission blocked",
  unavailable: "Location unavailable",
};
